/**
 * Les raccourcis de la garderie au tableau de bord de Gestion (demande du
 * 02/11/2026) : « pendant les périodes hors vacances, un raccourci vers les
 * listes d'ISA et de CSSA, qui débouche directement sur le jour en cours,
 * l'école et la plage — matin si on est le matin, ATL l'après-midi ».
 *
 * Un jour de garderie : du lundi au vendredi, dans l'année scolaire de l'école
 * (`garderie_annees`), pas un jour férié (`public_holidays`), et aucun stage ni
 * plaine ne tourne ce jour-là (les centres de vacances d'Éclosion ne tournent
 * qu'aux congés scolaires : c'est le signe des vacances que la base connaît,
 * PROPOSITION TECHNIQUE). Ici, la règle, sans la base. [cdv:garderie]
 */
import { jourDeLaSemaine } from './calcul';
import { plageDeLHeure } from './pointage';

export interface EcoleDeLAnnee {
  centerId: string;
  nom: string;
  tag: string;
  anneeScolaire: number;
  /** `AAAA-MM-JJ`. */
  debut: string;
  fin: string;
}

export interface PlageDuCatalogue {
  centerId: string;
  anneeScolaire: number;
  code: string;
  libelle: string;
  /** `HH:MM`. */
  heureDebut: string;
  heureFin: string;
  /** 1 = lundi … 5 = vendredi. */
  jours: number[];
}

export interface GarderieDuJour {
  centerId: string;
  nom: string;
  tag: string;
  /** `AAAA-MM-JJ`. */
  date: string;
  /** La plage que l'heure ouvre. */
  plage: { code: string; libelle: string; heureDebut: string; heureFin: string };
}

export interface ContexteDuJour {
  aujourdHui: string;
  /** `HH:MM` à Bruxelles. */
  heure: string;
  ecoles: readonly EcoleDeLAnnee[];
  plages: readonly PlageDuCatalogue[];
  /** Un jour férié aujourd'hui. */
  ferie: boolean;
  /** Un stage ou une plaine tourne aujourd'hui : congé scolaire. */
  vacances: boolean;
}

/** Pourquoi il n'y a pas de garderie aujourd'hui, ou `null` s'il y en a. */
export function pasDeGarderie(c: Pick<ContexteDuJour, 'aujourdHui' | 'ferie' | 'vacances'>): 'week-end' | 'ferie' | 'vacances' | null {
  if (jourDeLaSemaine(c.aujourdHui) > 5) return 'week-end';
  if (c.ferie) return 'ferie';
  if (c.vacances) return 'vacances';
  return null;
}

/** Les écoles où la garderie tourne aujourd'hui, avec la plage de l'heure ; rien hors des jours d'école. */
export function garderiesDuJour(c: ContexteDuJour): GarderieDuJour[] {
  if (pasDeGarderie(c)) return [];
  const jour = jourDeLaSemaine(c.aujourdHui);
  const out: GarderieDuJour[] = [];
  for (const e of c.ecoles) {
    if (c.aujourdHui < e.debut || c.aujourdHui > e.fin) continue;
    const duJour = c.plages.filter((p) => p.centerId === e.centerId && p.anneeScolaire === e.anneeScolaire && p.jours.includes(jour));
    const plage = plageDeLHeure(duJour, c.heure);
    if (!plage) continue;
    out.push({
      centerId: e.centerId,
      nom: e.nom,
      tag: e.tag,
      date: c.aujourdHui,
      plage: { code: plage.code, libelle: plage.libelle, heureDebut: plage.heureDebut, heureFin: plage.heureFin },
    });
  }
  return out.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}
